import React from 'react';

import { FilterGroup } from './GroupedRoutingFilterViewTypes';

export type UseAutoExpandGroupsArgs = Readonly<{
  groups: ReadonlyArray<FilterGroup>;
  isLoading: boolean;
  // Keys that qualify for conflict/unsaved auto-expansion under the current
  // state. Computed by the caller so this hook stays free of routing-filter
  // conflict semantics — it only knows "these keys should be open right now".
  conflictExpandKeys: ReadonlySet<string>;
}>;

export type UseAutoExpandGroups = Readonly<{
  expandedKeys: ReadonlySet<string>;
  toggleGroup: (key: string) => void;
}>;

// Owns the auto-expand policy for a grouped table: open a group when it gains a
// filter while mounted, and open/close the groups the caller flags via
// conflictExpandKeys — without disturbing groups the user opened by hand. Kept
// generic (no conflict/unsaved knowledge) so GroupedRoutingFilterView stays a
// reusable table; the routing-filter-specific key computation is the caller's.
export function useAutoExpandGroups(args: UseAutoExpandGroupsArgs): UseAutoExpandGroups {
  const { groups, isLoading, conflictExpandKeys } = args;

  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());
  const prevGroupCounts = React.useRef<Record<string, number>>({});
  const hasInitialized = React.useRef(false);
  // Keys this hook auto-expanded because of the caller's conflictExpandKeys.
  // Tracked separately so we can collapse them again when they stop qualifying,
  // without closing groups the user (or the count-based effect) expanded.
  const conflictExpandedKeys = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (isLoading) { return; }

    const recordCounts = () => {
      const nextCounts: Record<string, number> = {};
      for (const group of groups) { nextCounts[group.key] = group.filters.length; }
      prevGroupCounts.current = nextCounts;
    };

    // On the first render with real data, seed the baseline counts without auto-expanding —
    // otherwise every populated group would spring open on load.
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      recordCounts();
      return;
    }

    const prev = prevGroupCounts.current;
    const keysToExpand: string[] = [];
    for (const group of groups) {
      const prevCount = prev[group.key] || 0;
      if (group.filters.length > prevCount) {
        keysToExpand.push(group.key);
      }
    }
    if (keysToExpand.length > 0) {
      // Auto-expand the group that just gained a filter so the newly added row is revealed
      // in place. We intentionally do NOT programmatically scroll: the settings page nests
      // two scrollable containers (the SettingsCoordinator overflow region inside the page
      // scroll), and scrolling either one produced a jarring double-scroll.
      setExpandedKeys(current => {
        const next = new Set(current);
        for (const key of keysToExpand) { next.add(key); }
        return next;
      });
    }
    recordCounts();
  }, [groups, isLoading]);

  React.useEffect(() => {
    // The caller recomputes which groups qualify for auto-expansion under the
    // current conflict/unsaved state. This effect is authoritative over the keys
    // it previously opened: a group that no longer qualifies is collapsed again
    // so stale groups don't stay stuck open.
    const keysToExpand = new Set<string>(conflictExpandKeys);

    const prevAutoExpanded = conflictExpandedKeys.current;
    conflictExpandedKeys.current = keysToExpand;

    setExpandedKeys(current => {
      const next = new Set(current);
      keysToExpand.forEach(key => next.add(key));
      // Collapse only groups we auto-opened that no longer qualify — leave
      // groups the user opened manually (or the count effect opened) untouched.
      prevAutoExpanded.forEach(key => {
        if (!keysToExpand.has(key)) { next.delete(key); }
      });
      return next;
    });
  }, [conflictExpandKeys]);

  const toggleGroup = React.useCallback((key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (prev.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  }, []);

  return { expandedKeys, toggleGroup };
}
